/**
 * Honorários da área do Secretário: a cláusula como sai no contrato e o valor
 * por extenso.
 *
 * `clausulaDeHonorarios` ESPELHA `clausula_de_honorarios` do backend
 * (`services/secretario/instrument_context.py`) — é ele que monta o texto do
 * contrato; aqui só se mostra ao advogado, enquanto ele preenche, o que vai sair.
 * Os testes dos dois lados usam os mesmos casos; mudar um exige mudar o outro.
 */

export type Honorarios = Partial<
  Record<'valor' | 'valor_extenso' | 'forma_pagamento' | 'percentual_exito' | 'condicoes', string>
>;

/** O campo sem o ponto final — ele é reposto na junção, no lugar certo. */
function campo(honorarios: Honorarios, nome: keyof Honorarios): string {
  return (honorarios[nome] ?? '').trim().replace(/\.+$/, '').trim();
}

export function clausulaDeHonorarios(honorarios: Honorarios): string {
  const valor = campo(honorarios, 'valor');
  const extenso = campo(honorarios, 'valor_extenso');
  const forma = campo(honorarios, 'forma_pagamento');
  const exito = campo(honorarios, 'percentual_exito');
  const condicoes = campo(honorarios, 'condicoes');

  // Valor, forma e êxito compõem UMA frase (vírgula); as condições, outra.
  const frase: string[] = [];
  if (valor) {
    const principal = extenso ? `${valor} (${extenso})` : valor;
    frase.push(forma ? `${principal}, ${forma}` : principal);
  } else if (forma) {
    frase.push(forma);
  }
  if (exito) frase.push(frase.length ? `além de ${exito}` : exito);

  const partes = [frase.join(', '), condicoes].filter(Boolean);
  const texto = partes.join('. ');
  return texto ? `${texto}.` : '';
}

/**
 * Lê um montante em reais escrito como a pessoa escreve: "R$ 1.500,00",
 * "1500", "1.500", "1500,5". Texto que não é só um número ("um salário
 * mínimo", "R$ 1.500 + 10%") devolve `null` — aí o extenso é a pessoa quem
 * escreve.
 */
export function lerValorEmReais(texto: string): number | null {
  const limpo = texto.trim().replace(/^R\$\s*/i, '').trim();
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/.test(limpo)) return null;
  const valor = Number(limpo.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(valor) && valor > 0 && valor < 1e12 ? valor : null;
}

const UNIDADES = [
  '', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove',
  'dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove',
];
const DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CENTENAS = [
  '', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos',
  'seiscentos', 'setecentos', 'oitocentos', 'novecentos',
];

/** 1 a 999 por extenso. */
function ate999(n: number): string {
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];
  if (c) partes.push(CENTENAS[c]);
  if (resto) {
    if (resto < 20) {
      partes.push(UNIDADES[resto]);
    } else {
      const d = Math.floor(resto / 10);
      const u = resto % 10;
      partes.push(u ? `${DEZENAS[d]} e ${UNIDADES[u]}` : DEZENAS[d]);
    }
  }
  return partes.join(' e ');
}

const ESCALAS: [singular: string, plural: string][] = [
  ['', ''],
  ['mil', 'mil'],
  ['milhão', 'milhões'],
  ['bilhão', 'bilhões'],
];

/** Inteiro positivo por extenso ("mil e quinhentos", "um milhão e duzentos mil"). */
function inteiroPorExtenso(n: number): string {
  const grupos: number[] = [];
  for (let resto = n; resto > 0; resto = Math.floor(resto / 1000)) grupos.push(resto % 1000);

  const partes: { texto: string; valor: number }[] = [];
  for (let i = grupos.length - 1; i >= 0; i--) {
    const g = grupos[i];
    if (!g) continue;
    const [singular, plural] = ESCALAS[i];
    // "mil", nunca "um mil".
    const numero = i === 1 && g === 1 ? '' : ate999(g);
    const escala = i === 0 ? '' : g === 1 ? singular : plural;
    partes.push({ texto: [numero, escala].filter(Boolean).join(' '), valor: g });
  }
  // Entre grupos: "e" quando o grupo seguinte é menor que cem ou centena redonda
  // ("mil e quinhentos", "dois mil e trinta"); senão só espaço ("mil duzentos e trinta").
  return partes
    .map((p, i) => (i === 0 ? p.texto : `${p.valor < 100 || p.valor % 100 === 0 ? 'e ' : ''}${p.texto}`))
    .join(' ');
}

/** Montante em reais por extenso: "mil e quinhentos reais", "um milhão de reais", "dez reais e cinquenta centavos". */
export function valorPorExtenso(valor: number): string {
  const centavosTotais = Math.round(valor * 100);
  const reais = Math.floor(centavosTotais / 100);
  const centavos = centavosTotais % 100;

  const partes: string[] = [];
  if (reais) {
    // "um milhão DE reais": milhão/bilhão redondo pede o "de".
    const redondoEmMilhao = reais >= 1_000_000 && reais % 1_000_000 === 0;
    partes.push(`${inteiroPorExtenso(reais)} ${redondoEmMilhao ? 'de ' : ''}${reais === 1 ? 'real' : 'reais'}`);
  }
  if (centavos) partes.push(`${ate999(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`);
  return partes.join(' e ');
}
